import { useState, useRef, useEffect } from 'react';
import { Search, SlidersHorizontal, ChevronDown, Calendar, X } from 'lucide-react';
import Button from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface PostFiltersProps {
  onFilterChange: (filters: {
    tier?: string;
    dateRange?: {
      startDate: string;
      endDate: string;
    };
    search?: string;
  }) => void;
}

export default function PostFilters({ onFilterChange }: PostFiltersProps) {
  const { t } = useTranslation();
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({ startDate: '', endDate: '' });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update filters when any filter changes
  useEffect(() => {
    const filters: any = {};

    if (tierFilter !== 'all') {
      filters.tier = tierFilter;
    }

    if (dateRange.startDate || dateRange.endDate) {
      filters.dateRange = dateRange;
    }

    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }

    onFilterChange(filters);
  }, [tierFilter, dateRange, searchTerm, onFilterChange]);

  const handleTierChange = (tier: string) => {
    setTierFilter(tier);
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearDateRange = () => {
    setDateRange({ startDate: '', endDate: '' });
  };

  const getTierDisplayText = () => {
    switch (tierFilter) {
      case 'public':
        return t('posts.filters.public');
      case 'subscribers':
        return t('posts.filters.members');
      default:
        return t('posts.filters.tier');
    }
  };

  const getDateDisplayText = () => {
    if (dateRange.startDate && dateRange.endDate) {
      return `${dateRange.startDate} - ${dateRange.endDate}`;
    } else if (dateRange.startDate) {
      return `${t('posts.filters.from')} ${dateRange.startDate}`;
    } else if (dateRange.endDate) {
      return `${t('posts.filters.until')} ${dateRange.endDate}`;
    }
    return t('posts.filters.date');
  };

  const hasActiveFilters = tierFilter !== 'all' || dateRange.startDate || dateRange.endDate || searchTerm.trim();

  // flex-wrap, plus a real minimum on the search box. Without both this row is
  // rigid at ~394px: two min-w-[140px] buttons + gaps + the search input's own
  // 56px of padding do not fit the ~326px a 390px phone has left after two nested
  // px-4 containers, so the whole PAGE scrolled sideways on every fund page.
  // Measured 2026-09-24: documentElement.scrollWidth 394 at viewports 360/375/390.
  // Guarded by scripts/check-phone-width.mjs.
  return (
    <div className="flex flex-wrap items-center gap-3 mb-8">
      {/* Tier Filter Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="flex items-center gap-2 min-w-[140px] justify-between"
          >
            {getTierDisplayText()}
            <ChevronDown className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem onClick={() => handleTierChange('all')}>
            {t('posts.filters.all')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleTierChange('public')}>
            {t('posts.filters.public')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleTierChange('subscribers')}>
            {t('posts.filters.members')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Date Range Filter */}
      <div className="relative" ref={datePickerRef}>
        <Button
          variant="outline"
          className="flex items-center gap-2 min-w-[140px] justify-between"
          onClick={() => setShowDatePicker(!showDatePicker)}
        >
          <Calendar className="w-4 h-4" />
          {getDateDisplayText()}
          <ChevronDown className="w-4 h-4" />
        </Button>

        {showDatePicker && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50 min-w-[300px]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">{t('posts.filters.selectDateRange')}</h3>
              {(dateRange.startDate || dateRange.endDate) && (
                <button
                  onClick={clearDateRange}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  {t('posts.filters.clear')}
                </button>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('posts.filters.startDate')}</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('posts.filters.endDate')}</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                  min={dateRange.startDate}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="outline"
          className="flex items-center"
          onClick={() => {
            setTierFilter('all');
            setDateRange({ startDate: '', endDate: '' });
            setSearchTerm('');
          }}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </Button>
      )}

      {/* Search Input */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="search"
          placeholder={t('posts.filters.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}